import { toValue, type MaybeRefOrGetter } from 'vue';
import { UseIntersectionObserverOptions } from '@vueuse/core';
import type { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import { SnowplowContext, SnowplowLinkContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { generateUUIDv4 } from '@/helpers/uuid-helper';
import { captureMessageForSentry } from '@/libraries/sentry';

export interface VueObserveVisibilityOptions {
	callback?: (isVisible: boolean, entry: IntersectionObserverEntry) => void;
	once?: boolean;
	throttle?: number;
	throttleOptions?: {
		leading?: 'visible' | 'hidden' | 'both';
	};
	intersection?: UseIntersectionObserverOptions;
}

export interface VisibilityChangedPayload<T> {
	element: T;
	isVisible: boolean;
}

export type OnIntersectionCallback<T> = (payload: VisibilityChangedPayload<T>) => void;

export type AdditionalProperties = Omit<TrackingProviderPropertiesInterface, 'action' | 'nonInteraction' | 'category'>;

/**
 * According to MRC, a display ad will be considered “viewable”
 * if 50% of the ad creative is visible for at least one
 * second in the viewable space of the browser.
 */
export const mrcObservableOptions: VueObserveVisibilityOptions = {
	once: false,
	throttle: 1000,
	intersection: {
		root: null,
		rootMargin: '0px 0px 0px 0px',
		threshold: 0.5, // 50% visibility of each element
	},
} as const;

const properties: TrackingProviderPropertiesInterface = { action: 'impression', nonInteraction: true } as const;

/** Options to initialise the generic batch impression tracker for a category. */
interface UseBatchImpressionTrackingOptions<T> {
	/**
	 * Tracking category for the impression events.
	 * Contexts will be grouped according to this.
	 *
	 * e.g. you can group them by 'offer' impression and then further group them on entityId like: `offer_tm3959302`.
	 * this way, we won't mix title contexts with other impression batches.
	 */
	category: string;
	/**
	 * Transform the impressed elements into one or more tracking contexts.
	 */
	toContext: (element: T) => SnowplowContext | SnowplowContext[];
	/**
	 * Callback fired when the element becomes visible.
	 */
	onVisibilityCallback?: (element: T) => void | Promise<void>;

	/**
	 * Passes the method to update the offerImpressionUUID on the element (expected to be a ClickoutUrl).
	 * This uuid must be set in the batch queue, and is shared between all elements in the batch.
	 * Setting the uuid resolves the promise created by createUUIDPromise, allowing the clickout event to be sent.
	 */
	getUuidSetterForElement?: (element: T) => (uuid: string) => void;

	/** Creates a promise that is resolved when the batch queue is processed. Prevents clickout event being sent until offerImpressionUUID is updated */
	createUUIDPromise?: (element: T) => void;
}

/** Options for category specific impression tracking composables.  */
export interface BatchImpressionTrackingOptions {
	/** Manage visibility intersection, thresholds, and more. */
	observableOptions?: VueObserveVisibilityOptions;

	/** Extra contexts to include in the sent impression event. */
	additionalContexts?: MaybeRefOrGetter<SnowplowContext[]>;

	/** Extra tracking properties to include in the sent impression event. */
	additionalProperties?: MaybeRefOrGetter<AdditionalProperties>;

	/** in case we switch offer impressions of different titles, we need to differentiate those */
	groupId?: MaybeRefOrGetter<string>;

	/**  prevent duplicate context only takes latest version of the additional contexts */
	preventDuplicateAdditionalContext?: boolean;

	/** Callback to pass the offerImpressionUUID to the clickoutUrl, if applicable. */
	setImpressionUuidOnElement?: (uuid: string) => void;
}

// queue explanation:
// [CATEGORY, GROUP_ID, ELEMENT_CONTEXT, [ADDITIONAL_CONTEXTS]]
// - CATEGORY: category string for trackEvent category
// - GROUP_ID: further grouping of the batches, e.g. when using different titles
// - CONTEXT_ELEMENT: the element that has been seen
// - ADDITIONAL_CONTEXTS: de-duplicated contexts that will be sent once along with the CATEGORY
// - ON_INTERSECTION_CONTEXTS: contexts that will be sent with the CONTEXT_ELEMENT, added after the element is seen. Added separately to ADDITIONAL_CONTEXTS to avoid de-duplication.
//
// e.g. ['offer', 'tm5333', ClickoutContext, [TitleContext], [ModuleContext], trackingProperties , preventDuplicateAdditionalContext]
const queues = [] as [
	string,
	string,
	SnowplowContext[] | undefined,
	SnowplowContext[],
	SnowplowContext[],
	((uuid: string) => void) | undefined,
	AdditionalProperties,
	boolean
][];

type GroupImpression = {
	category: string;
	elements: Map<SnowplowContext, SnowplowContext>;
	additionalContexts: Map<SnowplowContext, SnowplowContext>;
	additionalProperties: AdditionalProperties;
	impressionUUID: string;
};

function toGroupId(category: string, groupId: string, { label, property, value }: AdditionalProperties) {
	// create a unique group id for different event types
	return [category, groupId, label, property, value].filter(Boolean).join('_');
}

// we need to create 1 persistent timer that can be used all the time.
// this way we don't have to re-create it and handle asyncness problems.
async function groupQueuedImpressions() {
	// queues is a dynamic array that can be modified while processing,
	// so its safer to take a snapshot of the current state
	const queuesSnapshot = [...queues];
	const currentLength = queuesSnapshot.length;

	const groups = queuesSnapshot.reduce((root, item, index) => {
		const [
			category,
			groupId,
			element,
			additionalContexts,
			onIntersectionContexts,
			setImpressionUuidOnElement,
			additionalProperties,
			preventDuplicateAdditionalContext,
		] = item;

		const id = toGroupId(category, groupId, additionalProperties);
		if (!(id in root)) {
			const impressionUUID = generateUUIDv4();

			// create group entry if it hasn't been created yet
			root[id] = {
				category,
				elements: new Map(),
				additionalContexts: new Map(),
				additionalProperties: {},
				impressionUUID,
			};

			if (setImpressionUuidOnElement) {
				// LinkContext is only needed for offer impressions (not titles),
				// Currenlty, we would only expect this to be a required context when the impressionUUID is also being passed to the element (clickoutUrl).
				const linkContext = new SnowplowLinkContext({ self: impressionUUID });
				root[id].elements.set(linkContext, linkContext);
			}
		}

		if (element) {
			element.forEach(ctx => root[id].elements.set(ctx, ctx));
		}

		if (setImpressionUuidOnElement) {
			setImpressionUuidOnElement(root[id].impressionUUID);
		}

		if (additionalProperties) {
			root[id].additionalProperties = additionalProperties;
		}

		// add additionalContexts if they are not already present or if duplicates are allowed
		if (!preventDuplicateAdditionalContext || !root[id].additionalContexts.size) {
			(toValue(additionalContexts) || []).forEach(context => root[id].additionalContexts.set(context, context));
		}
		// add onIntersectionContexts
		(toValue(onIntersectionContexts) || []).forEach(context => root[id].additionalContexts.set(context, context));

		return root;
	}, {} as Record<string, GroupImpression>);

	// update queue to remove processed items
	queues.splice(0, currentLength);

	return groups;
}

let processingPromise: boolean = false;

async function processQueue() {
	// Mutex on queue processing to prevent multiple concurrent executions
	if (processingPromise) return;

	try {
		processingPromise = true;
		const groups = await groupQueuedImpressions();
		Object.values(groups).forEach(group => sendGroupImpressionEvent(group));
	} catch (error: any) {
		captureMessageForSentry(
			'[processQueue() error - batch impressions]:',
			{ error, where: '[useBatchImpressionTracking.ts]: processQueue()' },
			'error'
		);
	} finally {
		processingPromise = false;
	}
}

// Exported function to flush the batched impressions immediately
// Used on clickout events to limit effect of browser throttling, as clickout event awaits the batch queue to be processed
export const flushBatchedImpressions = processQueue;
if (import.meta.client) {
	// process the queue every second
	setInterval(async () => {
		await processQueue();
	}, 1000);
}

async function addQueue(
	category: string,
	groupId: string,
	contextElements: SnowplowContext[] | undefined,
	additionalContexts: SnowplowContext[],
	onIntersectionContexts: SnowplowContext[],
	getUuidSetterForElement: ((uuid: string) => void) | undefined,
	additionalProperties: AdditionalProperties,
	preventDuplicateAdditionalContext: boolean
) {
	queues.push([
		category,
		groupId,
		contextElements,
		additionalContexts,
		onIntersectionContexts,
		getUuidSetterForElement,
		additionalProperties,
		preventDuplicateAdditionalContext,
	]);
}

/** Sends the impression event with the batched contexts for the current category. */
function sendGroupImpressionEvent(group: GroupImpression) {
	if (!group.elements || group.elements.size === 0) return;
	const contexts = Array.from(group.elements.values()).concat(...group.additionalContexts.values());
	TrackingHelper.trackEvent(group.category, { ...properties, ...group.additionalProperties }, contexts);
}

/**
 * Track a large number of impression events together.
 * Avoid using this directly, instead use it to create impression
 * tracking composables for specific kinds of elements.
 */
export function useBatchImpressionTracking<T>({
	category,
	toContext,
	observableOptions = mrcObservableOptions,
	additionalContexts = [] as SnowplowContext[],
	groupId = '',
	onVisibilityCallback,
	additionalProperties = {},
	getUuidSetterForElement = undefined,
	createUUIDPromise = undefined,
	preventDuplicateAdditionalContext = false,
}: UseBatchImpressionTrackingOptions<T> & BatchImpressionTrackingOptions) {
	// onIntersctionContexts are additionalContexts that require element information to be created
	function onIntersection(element: T, isVisible: boolean, onIntersectionContexts = [] as SnowplowContext[]) {
		// first we only gather, later we will de-duplicate
		if (isVisible) {
			const result = onVisibilityCallback?.(element);

			if (createUUIDPromise) createUUIDPromise(element);

			// onVisibilityCallback can be async - if so, we wait for it.
			// If it's not async, Promise.resolve() will resolve immediately.
			Promise.resolve(result).finally(async () => {
				// we can safely add additionalContexts to all queue entries separately, additionalContexts they get de-duplicated per group
				await addQueue(
					category,
					toValue(groupId),
					[toContext(element)].flat(),
					toValue(additionalContexts),
					toValue(onIntersectionContexts),
					getUuidSetterForElement && getUuidSetterForElement(element),
					toValue(additionalProperties),
					preventDuplicateAdditionalContext
				);
			});
		}
	}

	/** Single property if you don't need to override options. */
	function onVisibilityChanged(
		element: T,
		onIntersectionContexts = [] as SnowplowContext[]
	): VueObserveVisibilityOptions {
		return {
			...observableOptions,
			callback: (isVisible: boolean) => onIntersection(element, isVisible, onIntersectionContexts),
		};
	}

	return {
		/** Single property if you don't need to override options. */
		onVisibilityChanged,

		/** The exact callback called whenever visibility changes. */
		onIntersection,
	};
}
